<template>
    <BaseCard v-if="!dismissed" as="section" padding="sm" radius="lgr" class="banner" aria-labelledby="mobile-app-banner-title">
        <div class="intro">
            <div class="app-mark" aria-hidden="true"></div>
            <div class="copy">
                <h2 id="mobile-app-banner-title" class="heading">{{ t("chat.home.mobileAppBanner.heading") }}</h2>
                <p class="subtitle">{{ t("chat.home.mobileAppBanner.subtitle") }}</p>
            </div>
        </div>
        <a :href="downloadUrl" class="download base-btn-primary base-btn-sm" @click.prevent="handleDownload">{{ t("chat.home.mobileAppBanner.download") }}</a>
        <BaseButton theme="tertiary" size="sm" icon-only class="dismiss" :aria-label="t('chat.home.mobileAppBanner.dismiss')" @click="dismiss">
            <i class="bi bi-x-lg" aria-hidden="true"></i>
        </BaseButton>
    </BaseCard>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import { useUIInteractions } from "~vue/composables/useUIInteractions";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
    downloadUrl: string;
    storeName: string;
}>();

const { t } = useI18n();

const dismissed = ref(false);
const { createInteraction } = useUIInteractions();

const analyticsData = { store_name: props.storeName };

const persistDismissal = async () => {
    dismissed.value = true;
    await createInteraction("mobile_app_banner_dismissed");
};

const handleDownload = async () => {
    void logUserInteraction(AmplitudeEventNames.MOBILE_APP_BANNER_DOWNLOAD_CLICKED, analyticsData);
    await persistDismissal();
    router.visit(props.downloadUrl);
};

const dismiss = async () => {
    void logUserInteraction(AmplitudeEventNames.MOBILE_APP_BANNER_DISMISSED, analyticsData);
    await persistDismissal();
};
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .banner {
        @apply -mx-4 flex items-center gap-1 bg-surface-raised text-ink shadow-sm sm:mx-0 sm:gap-2;
    }

    .intro {
        @apply flex min-w-0 grow items-stretch gap-2 sm:gap-2;
    }

    .app-mark {
        @apply w-2.5 shrink-0 self-stretch rounded-full;
        background: linear-gradient(
            to bottom,
            var(--color-mobile-app-mark-1) 0%,
            var(--color-mobile-app-mark-2) 24%,
            var(--color-mobile-app-mark-3) 43%,
            var(--color-mobile-app-mark-4) 60%,
            var(--color-mobile-app-mark-5) 76%,
            var(--color-mobile-app-mark-3) 100%
        );
    }

    .copy {
        @apply flex min-w-0 grow flex-col;
    }

    .heading {
        @apply body-serif text-balance;
    }

    .subtitle {
        @apply details-big text-ink-muted;
    }

    .download {
        @apply flex shrink-0 justify-center;
    }

    .dismiss {
        @apply shrink-0;
    }
}
</style>
